import React, { Component } from 'react'
import PropTypes from 'prop-types'
import Tooltip from 'react-tooltip'
import { Portal } from 'react-portal'
import Media from 'sb-media'
import { DragElement } from 'sb-react-dnd'
import { injectIntl } from 'sb-react-intl'
import { constants } from 'common/helpers'
import actions from 'redux/actions'
import cx from 'classnames'

import SrcSize from 'components/ui/SrcSize/SrcSize'
import CloseIcon from 'components/ui/CloseIcon/CloseIcon'
import UpchargePrice from 'components/UpchargePrice/UpchargePrice'

import Label from '../Label/Label'

import messages from './messages'
import s from './Product.scss'


class QueueProduct extends Component {

  static propTypes = {
    data: PropTypes.shape({
      status: PropTypes.string,
      id: PropTypes.number,
      index: PropTypes.number,
      brand: PropTypes.string,
      name: PropTypes.string,
      image: PropTypes.string,
      isUnavailable: PropTypes.bool,
      isEmpty: PropTypes.bool,
      isFree: PropTypes.bool,
      upchargePrice: PropTypes.number,
      disabled: PropTypes.bool,
    }),
    viewOnly: PropTypes.bool,
    isMultipleProductPlan: PropTypes.bool,
    isQueueEmptyPlusCellEnabled: PropTypes.bool,
    isQueueEmptyFirstCellPlusEnabled: PropTypes.bool,
    square: PropTypes.bool,
    date: PropTypes.object.isRequired,
    onClick: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    onBlur: PropTypes.func,
    onKeyDown: PropTypes.func,
  }

  handleEmptyClick = () => {
    const { onClick } = this.props

    onClick()
  }

  renderEmpty() {
    const {
      intl, data, isMultipleProductPlan, viewOnly, date,
      isQueueEmptyPlusCellEnabled, isQueueEmptyFirstCellPlusEnabled,
    } = this.props
    const { isFree, status, isAvailableForAdd } = data

    const isPlusIconVisible = isAvailableForAdd && (isQueueEmptyPlusCellEnabled || isQueueEmptyFirstCellPlusEnabled)
    const isAlternativePlusIcon = isPlusIconVisible && isQueueEmptyFirstCellPlusEnabled

    const emptyClassName = cx(s.empty, {
      [s.free]: isFree,
      [s.multipleProductPlan]: isMultipleProductPlan,
      [s.emptyBottle]: !isPlusIconVisible,
      [s.emptyPlus]: isPlusIconVisible,
    })

    const buttonAreaLabel = intl.formatMessage(messages.buttonAreaLabel, { date: date.format('MMMM YYYY') })

    return (
      <button
        className={emptyClassName}
        type="button"
        aria-label={buttonAreaLabel}
        data-testid={constants.testIds.queue.emptyProduct}
        onClick={this.handleEmptyClick}
      >
        {
          isPlusIconVisible && (
            <span className={isAlternativePlusIcon ? s.alternativePlusIcon : s.plusIcon} aria-hidden="true">
              <svg width="9" height="9" viewBox="0 0 9 9" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
                <path d="M0 4h9v1H0z" /><path d="M5 0v9H4V0z" />
              </svg>
            </span>
          )
        }
        {
          isFree && !viewOnly && (
            <Label message={messages[status]} isFree />
          )
        }
      </button>
    )
  }

  handleProductClick = () => {
    const { data: { id }, onClick } = this.props

    onClick({ id })
  }

  handleRemoveClick = (event) => {
    const { data: { id, index, name, brand }, onRemove } = this.props

    onRemove({ event, id, index, name, brand })
  }

  handleProductBlur = (event) => {
    const { data: { id, index, name, brand }, onBlur } = this.props

    if (typeof onBlur === 'function') {
      onBlur({ event, id, index, name, brand })
    }
  }

  handleProductFocus = (event) => {
    const { data: { id, index, name, brand }, onFocus } = this.props

    if (typeof onFocus === 'function') {
      onFocus({ event, id, index, name, brand })
    }
  }

  handleProductKeyDown = (event) => {
    const { data: { id, index, name, brand }, onKeyDown } = this.props

    if (typeof onKeyDown === 'function') {
      onKeyDown({ event, id, index, name, brand })
    }
  }

  handleUnavailableLabelClick = (event) => {
    event.stopPropagation()
    actions.models.modals.open(constants.modals.limitedEditionProducts.OutOfStock)
  }

  renderProduct() {
    const { data, viewOnly, isMultipleProductPlan, square, ariaDescribedby } = this.props
    const { index, id, brand, name, image, isUnavailable, isFree, upchargePrice, disabled } = data

    const productClassName = cx(s.product, {
      [s.free]: isFree,
      [s.multipleProductPlan]: isMultipleProductPlan,
      [s.unavailable]: isUnavailable,
      [s.viewOnly]: viewOnly,
    })

    return (
      <div className={productClassName} id={`product${id}${index}`} data-tip data-for={`tip${id}${index}`}>
        {
          Boolean(upchargePrice) && (
            <UpchargePrice className={s.upchargePrice} price={upchargePrice} small />
          )
        }
        {
          !disabled && !viewOnly && (
            <CloseIcon
              className={s.removeButton}
              dataTestId="closeButton"
              isFocusable={false}
              onClick={this.handleRemoveClick}
            />
          )
        }
        {
          isUnavailable && !viewOnly && (
            <Label message={messages.unavailable} onClick={this.handleUnavailableLabelClick} />
          )
        }
        <SrcSize src={image} h={square ? 100 : 50} mH={100}>
          {
            (src) => (
              <div
                className={s.productPreview}
                role="button"
                tabIndex={0}
                style={{ backgroundImage: `url(${src})` }}
                data-testid={constants.testIds.queue.product}
                aria-label={`${brand} ${name}`}
                aria-describedby={`product${id}${index} ${ariaDescribedby}`}
                onBlur={this.handleProductBlur}
                onFocus={this.handleProductFocus}
                onKeyDown={this.handleProductKeyDown}
                onClick={this.handleProductClick}
              />
            )
          }
        </SrcSize>
        <Media exceptMobile>
          <Portal>
            <Tooltip id={`tip${id}${index}`} effect="solid" place="top">
              {brand}
              <br />
              {name}
            </Tooltip>
          </Portal>
        </Media>
      </div>
    )
  }

  renderContent() {
    const { data: { isEmpty } } = this.props

    if (isEmpty) {
      return this.renderEmpty()
    }

    return this.renderProduct()
  }

  render() {
    const { data, viewOnly } = this.props
    const { disabled } = data

    if (viewOnly) {
      return this.renderContent()
    }

    return (
      <DragElement data={{ disabled }}>
        {this.renderContent()}
      </DragElement>
    )
  }
}


export default injectIntl(QueueProduct)
